import React, { useEffect, useState } from 'react';
import { GRADES } from '../utils/xpUtils';
import Icon from './Icon';

const GRADE_ICON = {
  f2:         'flag',
  rookie:     'target',
  challenger: 'lightning',
  champion:   'trophy',
};

/* 40 deterministic confetti particles */
const CONFETTI = Array.from({ length: 40 }, (_, i) => {
  const angle = (i / 40) * 360;
  const rad   = (angle * Math.PI) / 180;
  const dist  = 120 + (i % 4) * 45;
  return {
    id:     i,
    tx:     Math.round(Math.cos(rad) * dist),
    ty:     Math.round(Math.sin(rad) * dist),
    size:   4 + (i % 5) * 2,
    delay:  (i % 8) * 0.06,
    dur:    0.9 + (i % 4) * 0.15,
    color:  ['#e10600','#f97316','#facc15','#4ade80','#60a5fa','#ffffff','#e10600','#f97316'][i % 8],
    square: i % 4 === 0,
  };
});

const AUTO_CLOSE_MS = 7000;

const GradeUpOverlay = ({ grade, level, onClose }) => {
  const [mounted, setMounted]   = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 30);
    return () => clearTimeout(t);
  }, []);

  /* countdown bar */
  useEffect(() => {
    const start  = Date.now();
    const frame  = () => {
      const elapsed = Date.now() - start;
      const pct = Math.max(0, 100 - (elapsed / AUTO_CLOSE_MS) * 100);
      setProgress(pct);
      if (pct > 0) requestAnimationFrame(frame);
    };
    const raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  /* auto-close */
  useEffect(() => {
    const t = setTimeout(onClose, AUTO_CLOSE_MS);
    return () => clearTimeout(t);
  }, [onClose]);

  const nextGradeIdx = GRADES.findIndex(g => g.id === grade.id) + 1;
  const nextGrade    = GRADES[nextGradeIdx] || null;

  return (
    <>
      <style>{`
        @keyframes gu-backdrop {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes gu-card {
          0%   { transform: scale(0.4) translateY(60px); opacity: 0; }
          65%  { transform: scale(1.06) translateY(-6px); opacity: 1; }
          82%  { transform: scale(0.97) translateY(2px); }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes gu-icon {
          0%   { transform: scale(0) rotate(-20deg); opacity: 0; }
          60%  { transform: scale(1.3) rotate(6deg); opacity: 1; }
          80%  { transform: scale(0.9) rotate(-2deg); }
          100% { transform: scale(1) rotate(0); opacity: 1; }
        }
        @keyframes gu-label {
          0%   { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes gu-glow {
          0%,100% { text-shadow: 0 0 20px var(--gc), 0 0 40px var(--gc); }
          50%     { text-shadow: 0 0 40px var(--gc), 0 0 80px var(--gc), 0 0 120px var(--gc); }
        }
        @keyframes gu-particle {
          0%   { transform: translate(-50%,-50%) scale(1);   opacity: 1; }
          100% { transform: translate(calc(-50% + var(--ptx)), calc(-50% + var(--pty))) scale(0); opacity: 0; }
        }
        @keyframes gu-stripe {
          0%   { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
        @keyframes gu-pulse-ring {
          0%   { box-shadow: 0 0 0 0 var(--gc); opacity: 0.8; }
          70%  { box-shadow: 0 0 0 20px transparent; opacity: 0; }
          100% { box-shadow: 0 0 0 0 transparent; opacity: 0; }
        }
      `}</style>

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
        style={{
          animation: 'gu-backdrop 0.3s ease forwards',
          background: 'rgba(0,0,0,0.88)',
          backdropFilter: 'blur(10px)',
        }}
        onClick={onClose}
      >
        {/* Particles */}
        {mounted && CONFETTI.map(p => (
          <div
            key={p.id}
            style={{
              position: 'absolute',
              left: '50%', top: '50%',
              width:  p.size,
              height: p.square ? p.size : p.size * 0.5,
              borderRadius: p.square ? 2 : '50%',
              background: p.color,
              '--ptx': `${p.tx}px`,
              '--pty': `${p.ty}px`,
              animation: `gu-particle ${p.dur}s ${p.delay}s cubic-bezier(0.25,0.46,0.45,0.94) both`,
              pointerEvents: 'none',
            }}
          />
        ))}

        {/* Card */}
        <div
          className="relative w-full max-w-sm rounded-2xl overflow-hidden"
          style={{
            '--gc': grade.color,
            border: `2px solid ${grade.color}`,
            background: `linear-gradient(160deg, #0c0c10 0%, ${grade.color}1a 100%)`,
            boxShadow: `0 0 60px ${grade.color}50, 0 0 120px ${grade.color}20, inset 0 0 40px ${grade.color}08`,
            animation: 'gu-card 0.7s cubic-bezier(0.34,1.56,0.64,1) 0.1s both',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Pulse ring */}
          {mounted && (
            <div className="absolute inset-0 rounded-2xl pointer-events-none"
              style={{ '--gc': grade.color, animation: 'gu-pulse-ring 1.2s 0.4s ease-out both' }} />
          )}

          {/* Racing stripe */}
          <div style={{ height: 5, background: `linear-gradient(90deg, ${grade.color} 0%, transparent 80%)`,
            animation: 'gu-stripe 0.6s 0.2s ease-out both', transformOrigin: 'left' }} />

          {/* Checkered accent squares */}
          <div className="absolute top-0 right-0 flex opacity-20 pointer-events-none" style={{ gap: 0 }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} style={{ width: 10, height: 10, background: i % 2 === 0 ? '#fff' : 'transparent' }} />
            ))}
          </div>

          <div className="px-8 pt-6 pb-4 text-center">
            {/* Icon */}
            <div className="mb-3 inline-flex items-center justify-center"
              style={{ animation: 'gu-icon 0.8s cubic-bezier(0.34,1.56,0.64,1) 0.35s both', color: grade.color }}>
              <Icon name={GRADE_ICON[grade.id] || 'star'} size={64} strokeWidth={1.5} />
            </div>

            {/* Label */}
            <p className="text-[10px] font-black uppercase tracking-[0.35em] text-zinc-500 mb-2"
              style={{ animation: 'gu-label 0.4s 0.6s ease both' }}>
              Grade débloqué
            </p>

            {/* Grade name */}
            <h2 className="text-5xl font-black mb-1 leading-none"
              style={{
                color: grade.color,
                '--gc': grade.color,
                animation: 'gu-glow 2s 0.8s ease-in-out infinite, gu-label 0.5s 0.7s ease both',
              }}>
              {grade.label}
            </h2>

            {/* Level */}
            <p className="text-zinc-400 text-sm mb-2"
              style={{ animation: 'gu-label 0.4s 0.85s ease both', opacity: 0 }}>
              Niveau <span className="text-white font-black text-lg">{level}</span> atteint
            </p>

            {/* Congrats */}
            <p className="text-white font-black text-xl mb-5"
              style={{ animation: 'gu-label 0.4s 1s ease both', opacity: 0 }}>
              Félicitations ! 🎉
            </p>

            {/* Next grade hint */}
            {nextGrade && (
              <div className="mb-5 rounded-xl px-4 py-2 text-xs text-zinc-500 bg-zinc-800/60"
                style={{ animation: 'gu-label 0.4s 1.1s ease both', opacity: 0 }}>
                Prochain grade :{' '}
                <span className="font-black" style={{ color: nextGrade.color }}>{nextGrade.label}</span>
                {' '}· niv. {nextGrade.minLevel}
              </div>
            )}

            {/* CTA */}
            <button
              onClick={onClose}
              className="w-full py-3.5 rounded-xl font-black text-white text-base transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: `linear-gradient(135deg, ${grade.color} 0%, ${grade.color}bb 100%)`,
                animation: 'gu-label 0.4s 1.2s ease both',
                opacity: 0,
              }}>
              Continuer →
            </button>
          </div>

          {/* Countdown bar */}
          <div className="h-1 bg-zinc-800">
            <div className="h-full transition-none"
              style={{ width: `${progress}%`, background: grade.color, opacity: 0.5 }} />
          </div>
        </div>
      </div>
    </>
  );
};

export default GradeUpOverlay;
